import { useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { FloatingInput } from '@/components/ui/floating-input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { apiFetch, signInWithToken } from '@/utils'
import { useUser } from '@/hooks/use-user'
import { Loader2 } from 'lucide-react'

const loginFormSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters')
})

type LoginFormData = z.infer<typeof loginFormSchema>

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)

  const [, navigate] = useLocation()
  const { toast } = useToast()
  const { setUserDataLoading } = useUser()
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    try {
      const loginResponse = await apiFetch('/v1/users/login', {
        method: 'POST',
        body: JSON.stringify({
          email: data.email,
          password: data.password
        })
      })

      const { data: loginResult, error } = await loginResponse.json()

      if (!loginResult?.token) {
        throw new Error(error.message)
      }

      await signInWithToken(loginResult?.token)

      setUserDataLoading(true)

      toast({
        title: 'Success',
        description: 'Logged in successfully'
      })
    } catch (error) {
      console.error(error)
      toast({
        title: 'Sign in failed',
        description: error instanceof Error ? error.message : 'Unknown reason',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#18181B] flex items-center justify-center">
      <div className="container max-w-lg mx-auto p-4">
        <Card className="p-6 bg-[#3C3C3C] border-0">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#ffffff]">Welcome to Secrets Hideaway</h1>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingInput
                        {...field}
                        type="email"
                        autoComplete="email"
                        label="Email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingInput
                        type="password"
                        {...field}
                        autoComplete="current-password"
                        label="Password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>
          </Form>

          <div className="mt-4 text-center space-y-2 flex flex-col items-center">
            <p className="text-sm text-[#BABABB]">
              Don't have an account?{' '}
              <Button
                variant="link"
                className="p-0 h-auto font-semibold text-[#EF4444] hover:text-[#DC2626]"
                onClick={() => navigate('/signup')}>
                Sign up
              </Button>
            </p>

            <Button
              variant="link"
              className="p-0 h-auto font-semibold text-[#BABABB] hover:text-[#ffffff]"
              onClick={() => navigate('/forgot-password')}>
              Forgot password?
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}
